// @client/utils/installGlobalErrorLogging.ts

import { serverLog } from '@client/utils/serverLog.ts'

type Opts = {
    appVersion?: string
    getUserId?: () => string | null // optional
    dedupeWindowMs?: number
}

export function installGlobalErrorLogging(opts: Opts = {}) {
    const dedupeMs = opts.dedupeWindowMs ?? 3000
    const seen = new Map<string, number>()
    const now = () => Date.now()

    const shouldSend = (key: string) => {
        const last = seen.get(key) ?? 0
        if (now() - last < dedupeMs) return false
        seen.set(key, now())
        return true
    }

    // Some noisy, low-signal browser errors you may want to ignore:
    const ignoreMsgs = [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
    ]

    window.addEventListener('error', (e) => {
        try {
            const msg = e.message || String(e.error ?? 'unknown error')
            if (ignoreMsgs.some((s) => msg?.includes(s))) return

            const key = `${msg}|${e.filename}|${e.lineno}|${e.colno}`
            if (!shouldSend(key)) return

            serverLog.error('uncaught.exception', e.error ?? msg, {
                kind: 'window.error',
                message: msg,
                file: e.filename,
                line: e.lineno,
                col: e.colno,
                stack: (e.error && (e.error as Error).stack) || undefined,
                href: location.href,
                userId: opts.getUserId?.() ?? undefined,
                appVersion: opts.appVersion,
            })
        } catch {}
    })

    window.addEventListener('unhandledrejection', (e) => {
        try {
            const reason = e.reason instanceof Error ? e.reason : new Error(String(e.reason))
            const msg = reason.message || String(e.reason)
            const key = `unhandledrejection|${msg}|${reason.stack?.split('\n')[1] ?? ''}`
            if (!shouldSend(key)) return

            serverLog.error('unhandled.rejection', reason, {
                kind: 'window.unhandledrejection',
                message: msg,
                stack: reason.stack,
                href: location.href,
                userId: opts.getUserId?.() ?? undefined,
                appVersion: opts.appVersion,
            })
        } catch {}
    })
}
